import {DataStatus, useDialog} from 'platform/components';
import {HStack, Link} from 'platform/foundation';

import {useState} from 'react';

import {useGetWorkshopInterestQuery, useSetWorkshopInterestSourceMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCodeList} from '../../../hooks/useCodeList';
import {handleApiError} from '../../../utils/handleApiError';
import {EditSourceDialog} from '../../Source/components/EditSourceDialog';

interface WorkshopInterestSourceProps extends TestIdProps {
  recordId: string;
}

export function WorkshopInterestSource(props: WorkshopInterestSourceProps) {
  const {
    data: workshopInterest,
    isLoading: interestLoading,
    isError: interestError,
  } = useGetWorkshopInterestQuery({id: props.recordId});

  const [codes, {isLoading: codesLoading, isError: codesError}] = useCodeList(
    'workshop_interest_source'
  );

  const [setSource] = useSetWorkshopInterestSourceMutation();

  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(
    () => workshopInterest?.sourceCodeId ?? null
  );
  const [isOpen, setOpen, setClose] = useDialog();

  const usedSourceCodeId = workshopInterest?.sourceCodeId ?? null;

  const handleSave = () => {
    if (!selectedSourceId) {
      return;
    }

    setSource({
      id: props.recordId,
      setWorkshopInterestSourceRequestBody: {
        sourceCodeId: selectedSourceId,
      },
    })
      .unwrap()
      .then(() => {
        setClose();
      })
      .catch(handleApiError);
  };

  const handleCloseDialog = () => {
    setSelectedSourceId(usedSourceCodeId);
    setClose();
  };

  const handleOpenDialog = () => {
    if (usedSourceCodeId !== selectedSourceId) {
      setSelectedSourceId(usedSourceCodeId);
    }
    setOpen();
  };

  const selectedSource = codes?.find((code) => code.codeId === usedSourceCodeId) ?? null;

  return (
    <DataStatus isLoading={interestLoading || codesLoading} isError={interestError || codesError}>
      <HStack align="center" spacing={1}>
        <Link
          size="small"
          leftIcon="action/language"
          onClick={handleOpenDialog}
          title={selectedSource?.name ?? i18n.t('entity.businessCase.labels.source')}
          data-testid={suffixTestId('source', props)}
        />

        <EditSourceDialog
          isOpen={isOpen}
          onClose={handleCloseDialog}
          apiSource={usedSourceCodeId}
          selectedSource={selectedSourceId ?? null}
          sources={codes ?? []}
          data-testid={suffixTestId('editSource', props)}
          updateSource={(value) => setSelectedSourceId(value)}
          onSave={handleSave}
        />
      </HStack>
    </DataStatus>
  );
}
